import React, { useState, lazy, Suspense } from "react";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const MainCalculatorsList = React.lazy(() => import("./MainCalculators"));
const RelatedLinks = React.lazy(() => import("../RelatedLinks"));

const TITLE = "Calculatrice de Durée du Décès - Calculez le temps écoulé depuis le décès";
const DESCRIPTION =
  "Calculatrice de Durée du Décès - Calculez le temps écoulé depuis le décès pour calculer des dates, durees ou ages avec precision. Outil pratique pour vos demarches administratives, votre organisation personnelle et vos besoins du quotidien.";
const KEYWORDS =
  "de durée du décès le temps écoulé depuis le décès, calculatrice de durée du décès le temps écoulé depuis le décès, calcul en ligne, age, date, temps, calendrier, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/duration-since-death/";

const GREGORIAN_MONTHS = [
  "Janvier", "Février", "Mars", "Avril",
  "Mai", "Juin", "Juillet", "Août",
  "Septembre", "Octobre", "Novembre", "Décembre"
];

function DurationSinceDeathCalculatorGregorian() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);

  const [deathDateInput, setDeathDateInput] = useState({
    day: "1",
    month: "1",
    year: "2020"
  });

  const [durationGregorian, setDurationGregorian] = useState(null);

  const calculateDurationGregorian = async () => {
    setIsLoading(true);
    setError(null);
    setDurationGregorian(null);

    try {
      const deathDate = new Date(
        parseInt(deathDateInput.year),
        parseInt(deathDateInput.month) - 1,
        parseInt(deathDateInput.day)
      );

      const now = new Date();
      if (isNaN(deathDate.getTime()) || deathDate > now) {
        setError("La date du décès est invalide ou dans le futur.");
        setIsLoading(false);
        setStatusCode(400);
        return;
      }

      let diffMs = now - deathDate;
      const totalSeconds = Math.floor(diffMs / 1000);
      const remainingSeconds = totalSeconds % 60;
      const totalMinutes = Math.floor(totalSeconds / 60);
      const remainingMinutes = totalMinutes % 60;
      const totalHours = Math.floor(totalMinutes / 60);
      const remainingHours = totalHours % 24;

      let years = 0;
      let months = 0;
      let tempDate = new Date(deathDate.getTime());

      while (tempDate < now) {
        let nextMonth = new Date(tempDate.getFullYear(), tempDate.getMonth() + 1, tempDate.getDate());
        if (nextMonth <= now) {
          months++;
          tempDate = nextMonth;
        } else {
          break;
        }
      }

      years = Math.floor(months / 12);
      months = months % 12;
      let remainingDays = Math.floor((now - tempDate) / (1000 * 60 * 60 * 24));

      const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const totalWeeks = Math.floor(totalDays / 7);

      setDurationGregorian({
        years,
        months,
        days: remainingDays,
        hours: remainingHours,
        minutes: remainingMinutes,
        seconds: remainingSeconds,
        totalDays,
        totalWeeks,
        totalSeconds,
        totalMinutes,
        totalHours,
        deathDateFormatted: deathDate.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      });
    } catch (err) {
      setError(err.message);
      setStatusCode(500);
    }

    setIsLoading(false);
  };

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center p-3">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>

          <div className="border border-light p-3">
            <p>
              Cette calculatrice vous aide à calculer le temps exact écoulé depuis le décès d'une personne en calendrier grégorien. Elle affiche la durée en années, mois, jours, heures, minutes et secondes avec détails précis.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle p-3 bg-light rounded">
              <div className="p-2 mb-3 bg-success text-white">
                <strong>Entrez la date du décès</strong>
              </div>

              <div className="mb-3">
                <label><strong>Jour:</strong></label>
                <select
                  className="form-control"
                  value={deathDateInput.day}
                  onChange={(e) => setDeathDateInput({ ...deathDateInput, day: e.target.value })}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label><strong>Mois:</strong></label>
                <select
                  className="form-control"
                  value={deathDateInput.month}
                  onChange={(e) => setDeathDateInput({ ...deathDateInput, month: e.target.value })}
                >
                  {GREGORIAN_MONTHS.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label><strong>Année:</strong></label>
                <select
                  className="form-control"
                  value={deathDateInput.year}
                  onChange={(e) => setDeathDateInput({ ...deathDateInput, year: e.target.value })}
                >
                  {Array.from({ length: 150 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="text-center mb-4">
                <button
                  className="btn btn-success btn-lg px-5"
                  onClick={calculateDurationGregorian}
                  disabled={isLoading}
                >
                  {isLoading ? "Calcul en cours..." : "Calculer la Durée"}
                </button>
              </div>

              {error && <div className="alert alert-danger">{error}</div>}

              {durationGregorian && (
                <div className="alert alert-success mt-4">
                  <h3>Durée du Décès:</h3>
                  <div className="mb-3">
                    <strong style={{ fontSize: "24px", color: "#28a745" }}>
                      {durationGregorian.years} an(s), {durationGregorian.months} mois, {durationGregorian.days} jour(s)
                    </strong>
                  </div>
                  <div className="mt-3">
                    <p><strong>Détails complets:</strong></p>
                    <ul>
                      <li><strong>Date du décès:</strong> {durationGregorian.deathDateFormatted}</li>
                      <li><strong>Heures:</strong> {durationGregorian.hours}</li>
                      <li><strong>Minutes:</strong> {durationGregorian.minutes}</li>
                      <li><strong>Secondes:</strong> {durationGregorian.seconds}</li>
                      <li><strong>Jours totaux:</strong> {durationGregorian.totalDays}</li>
                      <li><strong>Semaines totales:</strong> {durationGregorian.totalWeeks}</li>
                      <li><strong>Heures totales:</strong> {durationGregorian.totalHours}</li>
                      <li><strong>Minutes totales:</strong> {durationGregorian.totalMinutes}</li>
                      <li><strong>Secondes totales:</strong> {durationGregorian.totalSeconds}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <br />
          <SecondAds />
          <br />
          <Suspense fallback={<div>Chargement...</div>}>
            <MainCalculatorsList />
            <br />
            <RelatedLinks />
          </Suspense>
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Calcul de durée depuis un décès</h3></div>
              <div className="info-section-body">
                <p>Cette calculatrice mesure le temps écoulé depuis la date d'un décès : années, mois, semaines et jours. Elle est utile pour les commémorations, les anniversaires de deuil, ou les démarches successorales qui dépendent du temps écoulé depuis le décès (prescription, délais de renonciation à une succession, etc.).</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Délais légaux liés au décès en France</h3></div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Déclaration de décès :</strong> 24 heures auprès de la mairie</li>
                  <li><strong>Délai d'acceptation/renonciation à la succession :</strong> 4 mois (prolongeable à 10 mois)</li>
                  <li><strong>Déclaration fiscale de succession :</strong> 6 mois en France métropolitaine</li>
                  <li><strong>Prescription de l'action en réduction :</strong> 5 ans à partir du décès</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DurationSinceDeathCalculatorGregorian;
